/**
 * No silent grey buttons (2026-10-30, director rule).
 *
 * A PA manager typed a note under "Assign" and thought the button was broken:
 * it was grey because the list still showed the current holder, and nothing on
 * the screen said so. The same shape had already stalled resident creation for
 * days. A button that cannot be pressed says why, in words, beside it -- and the
 * button names that line (aria-describedby), so a screen reader hears it too.
 *
 *   <WithReason reason={!to ? 'Pick an advocate first.' : null}>
 *     {(describedBy) => <button disabled={busy || !to} aria-describedby={describedBy}>Assign</button>}
 *   </WithReason>
 *
 * `reason` is the first unmet condition in plain words, or null when the button
 * may be pressed (or is only busy: a busy button says so in its own label).
 */
import React, { useId } from 'react';

export default function WithReason({ reason, children, className = 'pa-text none' }: {
  reason: string | null | false | undefined;
  children: (describedBy: string | undefined) => React.ReactNode;
  className?: string;
}) {
  const id = `why-${useId().replace(/:/g, '')}`;
  return (
    <>
      {children(reason ? id : undefined)}
      {reason ? <span id={id} className={className} data-disabled-reason="">{reason}</span> : null}
    </>
  );
}

/** The first condition that holds, as its words; null when none does. */
export const firstReason = (...rules: Array<[boolean, string]>): string | null => {
  const hit = rules.find(([cond]) => cond);
  return hit ? hit[1] : null;
};

/** The reason line alone, for a button whose markup stays where it is: give the button `aria-describedby={id}` when `reason` is set. */
export function Why({ id, reason, className = 'pa-text none' }: { id: string; reason: string | null | false | undefined; className?: string }) {
  return reason ? <span id={id} className={className} data-disabled-reason="">{reason}</span> : null;
}

/** A reason line's id, unique to this component instance: two open copies of one form never share it. */
export const useWhyId = (name: string): string => `${name}-${useId().replace(/:/g, '')}`;
